/*                                                   */
import type { ProblemJson } from "@kestrel/http";
import type kestrelStores from "@kestrel/stores";
import { getContext } from "svelte";
import { writable, type Readable, type Writable } from "svelte/store";
import type { ActionResult } from "@kestrel/actions";
import { noop } from "@otto-ec/assets-core-utils/misc";
import type { ResolvedComponent, SSRData } from "@kestrel/client";
import type { KestrelStores } from "@kestrel/stores";
import { cacheHeaders, headers, jsonMimes } from "../../utils/mimes";
import { decodeActionResultFromResponse } from "./utils/fetchUtils";

export const kestrelContext = "__kestrel__" as const;

/**
 *
 *
 *
 *
 *
 *
 */
export function createStores(
  state: SSRData,
  resolvedComponent: ResolvedComponent | undefined,
): KestrelStores {
  return {
    data: writable(state.data),
    form: writable(state.form),
    error: writable(state.error),
    routeId: writable(state.routeId),
    routePath: writable(state.routePath),
    navigate: writable([state.routeId, state.routePath, state.data, state.error, noop]),
    component: writable(resolvedComponent),
  };
}

/**
 *
 */
export const getStores = function getStores() {
  return getContext<kestrelStores.KestrelStores>(kestrelContext);
} satisfies typeof kestrelStores.getStores;

/**
 *
 *
 */
export function handleError(e: unknown, store?: Writable<unknown>): ProblemJson {
  const err = e as Error;

  const result: ProblemJson = {
    status: 0,
    title: err.message,
    detail: ((err.cause as Error) ?? err).stack,
    type: "unknown_error",
  };

  if (store) {
    store.set(result);
  }

  return result as ProblemJson;
}

/**
 *
 *
 *
 */
export async function fetchRequest(url: string | URL): Promise<ActionResult> {
  const resp = await fetch(url, {
    headers: {
      Accept: jsonMimes[0],
      [cacheHeaders[0]]: "no-cache",
      [headers[3]]: "true",
    },
    cache: "no-store",
  });

  return decodeActionResultFromResponse(resp);
}

/**
 *
 */
export const useLoadData = function useLoadData() {
  if (import.meta.env.SSR) return noop;

  return async (url: string | URL = window.location.href) => {
    try {
      const result = await fetchRequest(url);
      if (result.type === "error") return result.error;
      if (result.type === "redirect") return {};
      return result.data.data as any;
    } catch (e) {
      return handleError(e);
    }
  };
} satisfies typeof kestrelStores.useLoadData;

/**
 *
 */
export const useReloadData = function useReloadData() {
  if (import.meta.env.SSR) return noop;

  const { data, error } = getStores();

  return async (url = window.location.href) => {
    try {
      const result = await fetchRequest(url);

      if (result.type === "error") {
        error.set(result.error);
        return result.error;
      }

      if (result.type === "redirect") {
        window.location.assign(result.location);
        return {};
      }

      error.set(null);
      data.set(result.data.data);
      return result.data.data as any;
    } catch (e) {
      return handleError(e, error);
    }
  };
} satisfies typeof kestrelStores.useReloadData;

/**
 *
 */
export const data = {
  subscribe: (run, invalidate) => getStores().data.subscribe(run, invalidate),
} satisfies Readable<unknown>;
